import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import type { EntityClusterSummary } from '@/components/map/utils/entityClusterProperties';
import { toLngLatTuple } from '@/utils/mapUtils';

/** A bubble under the cursor: the rendered feature, its anchor and its hover key. */
export interface ClusterCardHit {
    feature: maplibregl.MapGeoJSONFeature;
    coords: [number, number];
    key: string;
}

export interface ClusterCardHoverOptions {
    /** The circle layers whose features carry a card. */
    layerIds: readonly string[];
    /**
     * The hover key of a feature, or null when it carries no card. Keys must be unique across
     * `layerIds` — include the layer id when ids are only unique per source.
     */
    keyFor: (feature: maplibregl.MapGeoJSONFeature, coords: [number, number]) => string | null;
    /** Reads the card's breakdown off the feature's accumulated counters. */
    readSummary: (properties: Record<string, unknown>) => EntityClusterSummary;
    /**
     * What a click on a bubble does once its card has been dropped. Optional: the single-client
     * clusters leave their expansion zoom to the layer hook that owns the source.
     */
    onClick?: (hit: ClusterCardHit, map: maplibregl.Map) => void;
}

export interface ClusterCardHoverState {
    /** The breakdown of the hovered bubble, or null when no card is showing. */
    hoveredSummary: EntityClusterSummary | null;
    /** Geographic [longitude, latitude] anchor of the hovered bubble — the Popup anchor. */
    hoveredLngLat: [number, number] | null;
    /** For the card: the cursor arrived, so a pending dismissal is cancelled. */
    onCardEnter: () => void;
    /** For the card: the cursor left it, so the card goes at once. */
    onCardLeave: () => void;
}

// Time the cursor has to travel from the bubble onto the card before the card goes.
const CARD_LEAVE_GRACE_MS = 150;

/**
 * The one hover behaviour behind every cluster bubble card, so the single-client map and the
 * Network Explorer read as one product: `mousemove` + queryRenderedFeatures for the bubbles
 * under the cursor, a key guard against redundant state updates, a short deferred dismissal
 * on leaving the bubble so the cursor can reach the card (whose enter/leave callbacks cancel
 * or force it), and an immediate dismissal on any camera movement, since the bubbles regroup
 * and the anchor would go stale. Hover is suppressed while the map is moving for the same
 * reason. Where bubbles overlap the hover is sticky: the bubble already showing its card
 * keeps it for as long as the cursor is on it, and the card moves on only once the cursor has
 * left that bubble, rather than flicking to whichever bubble is drawn on top.
 *
 * A click always drops the card first (the camera is about to move) and then does whatever
 * the caller asks. Listeners are registered once per enable — delegated listeners re-resolve
 * their layer on every event, so they survive a style reload; re-registering on `style.load`
 * would duplicate them (MapLibre dedupes plain listeners but not delegated ones).
 *
 * Tested through its two consumers, usePoiClusterInteractions and
 * useNetworkExplorerClusterInteractions, whose option sets are the only ones in the product.
 */
export function useClusterCardHover(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    enabled: boolean,
    options: ClusterCardHoverOptions,
): ClusterCardHoverState {
    // One value, so the card's breakdown and anchor can never disagree.
    const [hovered, setHovered] = useState<{ summary: EntityClusterSummary; lngLat: [number, number] } | null>(null);

    const hoveredKeyRef = useRef<string | null>(null);
    const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Holds the current dismiss function so the stable card callbacks never see a stale closure.
    const dismissRef = useRef<() => void>(() => {});
    // The options are read per event, so a caller may build them inline without the
    // listeners being torn down and re-registered on every render.
    const optionsRef = useRef(options);
    useLayoutEffect(() => {
        optionsRef.current = options;
    });

    const onCardEnter = useCallback(() => {
        if (leaveTimeoutRef.current !== null) {
            clearTimeout(leaveTimeoutRef.current);
            leaveTimeoutRef.current = null;
        }
    }, []);

    const onCardLeave = useCallback(() => {
        dismissRef.current();
    }, []);

    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;
        // Fixed for the life of this registration: the layers are what the listeners bind to.
        const layerIds = [...optionsRef.current.layerIds];

        dismissRef.current = () => {
            leaveTimeoutRef.current = null;
            hoveredKeyRef.current = null;
            setHovered(null);
        };

        const cancelPendingLeave = () => {
            if (leaveTimeoutRef.current !== null) {
                clearTimeout(leaveTimeoutRef.current);
                leaveTimeoutRef.current = null;
            }
        };

        // Every carded bubble under the cursor, topmost first. Only queries layers that
        // currently exist — queryRenderedFeatures throws on unknown ids.
        const hitsAt = (point: maplibregl.Point): ClusterCardHit[] => {
            const layers = layerIds.filter((id) => !!mlMap.getLayer(id));
            if (!layers.length) return [];
            return mlMap.queryRenderedFeatures(point, { layers }).flatMap((feature) => {
                if (feature.geometry.type !== 'Point') return [];
                const coords = toLngLatTuple(feature.geometry.coordinates);
                if (!coords) return [];
                const key = optionsRef.current.keyFor(feature, coords);
                return key === null ? [] : [{ feature, coords, key }];
            });
        };

        const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
            if (mlMap.isMoving()) return;
            cancelPendingLeave();

            const hits = hitsAt(e.point);
            if (!hits.length) return;
            // Sticky: the bubble showing its card keeps it while the cursor is still on it.
            if (hits.some((hit) => hit.key === hoveredKeyRef.current)) return;

            const hit = hits[0];
            hoveredKeyRef.current = hit.key;
            setHovered({ summary: optionsRef.current.readSummary(hit.feature.properties), lngLat: hit.coords });
        };

        const handleMouseLeave = () => {
            leaveTimeoutRef.current = setTimeout(() => {
                dismissRef.current();
            }, CARD_LEAVE_GRACE_MS);
        };

        const handleClick = (e: maplibregl.MapLayerMouseEvent) => {
            cancelPendingLeave();
            dismissRef.current();
            const onClick = optionsRef.current.onClick;
            if (!onClick) return;
            const hits = hitsAt(e.point);
            if (!hits.length) return;
            onClick(hits[0], mlMap);
        };

        // Any camera movement (including a click's own ease) dismisses the card at once,
        // cancelling a deferred dismissal so it cannot fire late.
        const handleMoveStart = () => {
            cancelPendingLeave();
            dismissRef.current();
        };

        for (const layerId of layerIds) {
            mlMap.on('mousemove', layerId, handleMouseMove);
            mlMap.on('mouseleave', layerId, handleMouseLeave);
            mlMap.on('click', layerId, handleClick);
        }
        mlMap.on('movestart', handleMoveStart);

        return () => {
            cancelPendingLeave();
            for (const layerId of layerIds) {
                mlMap.off('mousemove', layerId, handleMouseMove);
                mlMap.off('mouseleave', layerId, handleMouseLeave);
                mlMap.off('click', layerId, handleClick);
            }
            mlMap.off('movestart', handleMoveStart);
            dismissRef.current = () => {};
        };
    }, [enabled, isMapLoaded, mapRef]);

    // Disabling (leaving the mode, or the radial tool taking the map) drops the card, so
    // nothing stale shows when the hook is enabled again. Kept apart from the registration
    // effect above: a re-registration alone (a new map ref) must not blank an open card.
    useEffect(() => {
        if (enabled) return;
        hoveredKeyRef.current = null;
        // eslint-disable-next-line react-hooks/set-state-in-effect, react-x/set-state-in-effect
        setHovered(null);
    }, [enabled]);

    return {
        hoveredSummary: hovered?.summary ?? null,
        hoveredLngLat: hovered?.lngLat ?? null,
        onCardEnter,
        onCardLeave,
    };
}
